import React, { useContext, useEffect } from "react";
import { useImmer } from "use-immer";
import { errorMessage } from "../../../utils";
import {
  getDashboardCards,
  getDashboardSalesGraph,
  getDashboardStoreSales,
} from "../api";
import { addDays, differenceInCalendarDays } from "date-fns";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { TableStateContext } from "../../../store";

// the dashboard only ever loads up to this many days at once
export const DASHBOARD_MAX_RANGE_DAYS = 30;

// Keeps a picked range inside the cap by moving the end date to the last
// allowed day after the start. Reports whether it had to change anything.
const clampDateRange = (range) => {
  const { startDate, endDate } = range[0];
  const spanDays = differenceInCalendarDays(endDate, startDate) + 1;
  if (spanDays <= DASHBOARD_MAX_RANGE_DAYS) {
    return { range, clamped: false };
  }
  return {
    range: [
      { ...range[0], endDate: addDays(startDate, DASHBOARD_MAX_RANGE_DAYS - 1) },
    ],
    clamped: true,
  };
};

export const useDashboard = () => {
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const {
    filterState: {
      params: { dashboardDateRange },
    },
    filterStateDispatch,
  } = useContext(TableStateContext);

  const [state, setState] = useImmer({
    isCardsLoading: true,
    isSalesGraphLoading: true,
    isStoreSalesLoading: true,
    isOpen: false,
    date: [
      {
        startDate: addDays(new Date(), -6),
        endDate: new Date(),
        key: "selection",
      },
    ],
    showDate: [
      {
        startDate: addDays(new Date(), -6),
        endDate: new Date(),
        key: "selection",
      },
    ],
    salesGraph: [],
    storeSales: [],
    cardsData: [],
  });

  useEffect(() => {
    if (Object.keys(dashboardDateRange).length) {
      // a range saved before the cap existed is trimmed quietly on load
      const { range: date } = clampDateRange([
        {
          endDate: new Date(dashboardDateRange.endDate),
          startDate: new Date(dashboardDateRange.startDate),
          key: "selection",
        },
      ]);
      setState((draft) => {
        draft.date = date;
        draft.showDate = date;
      });
      invokeApis(date);
    } else {
      invokeApis(state.date);
    }
  }, []);

  const invokeApis = (date) => {
    HandleDashboardSalesGraph(date);
    HandleDashboardCards(date);
    if (!isStoreAdmin) {
      HandleDashboardStoreSales(date);
    }
  };

  const HandleDashboardCards = async (date) => {
    try {
      setState((draft) => {
        draft.isCardsLoading = true;
      });
      const res = await getDashboardCards(
        moment(date[0].startDate).format("yyyy-MM-DD"),
        moment(date[0].endDate).format("yyyy-MM-DD")
      );
      if (res.success) {
        setState((draft) => {
          draft.cardsData = res.data;
          draft.isCardsLoading = false;
        });
      } else {
        errorMessage(res.message);
        setState((draft) => {
          draft.isCardsLoading = false;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isCardsLoading = false;
      });
    }
  };

  const HandleDashboardSalesGraph = async (date) => {
    try {
      setState((draft) => {
        draft.isSalesGraphLoading = true;
      });
      const res = await getDashboardSalesGraph(
        moment(date[0].startDate).format("yyyy-MM-DD"),
        moment(date[0].endDate).format("yyyy-MM-DD")
      );
      if (res.success) {
        setState((draft) => {
          draft.salesGraph = res.data;
          draft.isSalesGraphLoading = false;
        });
      } else {
        errorMessage(res.message);
        setState((draft) => {
          draft.isSalesGraphLoading = false;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isSalesGraphLoading = false;
      });
    }
  };

  const HandleDashboardStoreSales = async (date) => {
    try {
      setState((draft) => {
        draft.isStoreSalesLoading = true;
      });
      const res = await getDashboardStoreSales(
        moment(date[0].startDate).format("yyyy-MM-DD"),
        moment(date[0].endDate).format("yyyy-MM-DD")
      );
      if (res.success) {
        setState((draft) => {
          draft.storeSales = res.data;
          draft.isStoreSalesLoading = false;
        });
      } else {
        errorMessage(res.message);
        setState((draft) => {
          draft.isStoreSalesLoading = false;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isStoreSalesLoading = false;
      });
    }
  };

  const onChangeDate = (e) => {
    const { range, clamped } = clampDateRange(e);
    if (clamped) {
      errorMessage(`Date range cannot exceed ${DASHBOARD_MAX_RANGE_DAYS} days`);
    }
    setState((draft) => {
      draft.date = range;
    });
  };

  const handleIsOpen = (e) => {
    setState((draft) => {
      draft.isOpen = e;
    });
  };

  // isStoreSalesLoading is only relevant for super admin; store admin never fetches store sales
  const isApiPending =
    state.isCardsLoading ||
    state.isSalesGraphLoading ||
    (!isStoreAdmin && state.isStoreSalesLoading);

  const handleDateChangeApply = () => {
    if (isApiPending) return;
    // safety net: nothing longer than the cap ever reaches the API
    const { range, clamped } = clampDateRange(state.date);
    if (clamped) {
      errorMessage(`Date range cannot exceed ${DASHBOARD_MAX_RANGE_DAYS} days`);
      setState((draft) => {
        draft.date = range;
      });
      return;
    }
    setState((draft) => {
      draft.showDate = state.date;
      draft.isOpen = false;
    });
    invokeApis(state.date);
    filterStateDispatch({
      type: "SET_DATA",
      page: "dashboardDateRange",
      data: state.date[0],
    });
  };

  useEffect(() => {
    if (!state.isOpen) {
      setState((draft) => {
        draft.date = state.showDate;
      });
    }
  }, [state.isOpen, state.showDate]);

  return {
    state,
    isStoreAdmin,
    isApiPending,
    onChangeDate,
    handleIsOpen,
    handleDateChangeApply,
  };
};
